import React, { useState, useEffect, useContext } from "react";
import {
  MessageSquare,
  Send,
  AlertCircle,
  CheckCircle,
  Clock,
  ChevronDown,
  MessageCircle,
  History,
} from "lucide-react";
import axios from "axios";
import config from "../config/environment";
import { handleApiError, logError } from "../utils/errorHandler";
import { AuthContext } from "../context/AuthContext";

const PremiumMessaging = ({ subscription }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    subject: "",
    message: "",
    category: "general",
    priority: "normal",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [messageHistory, setMessageHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const { userId, username } = useContext(AuthContext);

  // Check if user has premium access or had premium access (including cancelled users)
  const isPremiumUser =
    subscription &&
    (subscription.status === "active" ||
      subscription.status === "trialing" ||
      subscription.status === "canceled" ||
      subscription.status === "canceling" ||
      subscription.status === "past_due" ||
      subscription.status === "paused");

  useEffect(() => {
    if (isPremiumUser) {
      fetchCategories();
      fetchMessageHistory(); // Always load message history for premium users
    }
  }, [isPremiumUser]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        `${config.api.baseUrl}/api/user-messages/categories`,
        { timeout: config.api.timeout }
      );
      setCategories(response.data.data || []);
    } catch (err) {
      console.error("Error fetching categories:", err);
      // Use default categories if API fails
      setCategories([
        { value: "general", label: "General Inquiry" },
        { value: "technical", label: "Technical Support" },
        { value: "billing", label: "Billing Question" },
        { value: "feature_request", label: "Feature Request" },
      ]);
    }
  };

  const fetchMessageHistory = async () => {
    if (!isPremiumUser || !userId) return;

    setHistoryLoading(true);
    try {
      const response = await axios.get(
        `${config.api.baseUrl}/api/user-messages`,
        {
          params: { userId },
          timeout: config.api.timeout,
        }
      );
      setMessageHistory(response.data.data?.messages || []);
    } catch (err) {
      const handledError = handleApiError(err);
      console.error("Error fetching message history:", handledError);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.message.trim()) {
      setError("Please enter a message");
      return;
    }

    if (!isPremiumUser) {
      setError(
        "This feature is only available to current and former premium subscribers"
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${config.api.baseUrl}/api/user-messages`,
        {
          userId,
          email: username,
          subject: formData.subject.trim() || null,
          message: formData.message.trim(),
          category: formData.category,
          priority: formData.priority,
        },
        { timeout: config.api.timeout }
      );

      setSuccess(true);
      setFormData({
        subject: "",
        message: "",
        category: "general",
        priority: "normal",
      });

      // Refresh message history to show the new message
      fetchMessageHistory();

      // Auto-close success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
        setIsOpen(false);
      }, 3000);
    } catch (err) {
      const handledError = handleApiError(err);
      setError(handledError.message);
      logError(handledError, {
        context: "premium_messaging",
        userId,
        category: formData.category,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getCategoryLabel = (value) => {
    const category = categories.find((cat) => cat.value === value);
    return category ? category.label : value;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgent":
        return "text-red-600 bg-red-100";
      case "high":
        return "text-orange-600 bg-orange-100";
      default:
        return "text-blue-600 bg-blue-100";
    }
  };

  // Don't render if user doesn't have premium access
  if (!isPremiumUser) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Message History Section - Always visible for premium users */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="mr-3 rounded-full bg-blue-100 p-2 text-blue-600">
              <History className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Your Messages
              </h3>
              <p className="text-sm text-gray-500">
                View all messages you've sent to our support team
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full">
              {messageHistory.length}{" "}
              {messageHistory.length === 1 ? "Message" : "Messages"}
            </span>
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
              Subscriber Feature
            </span>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg">
          <div className="max-h-96 overflow-y-auto">
            {historyLoading ? (
              <div className="p-8 text-center text-gray-500">
                <Clock className="w-8 h-8 mx-auto mb-3 animate-spin" />
                <p>Loading your messages...</p>
              </div>
            ) : messageHistory.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <h4 className="text-lg font-medium text-gray-700 mb-2">
                  No messages yet
                </h4>
                <p className="text-sm">
                  Send your first message using the form below!
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {messageHistory.map((msg) => (
                  <div
                    key={msg.id}
                    className="p-6 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h4 className="text-base font-medium text-gray-900">
                            {msg.subject || "No Subject"}
                          </h4>
                          <span
                            className={`px-3 py-1 text-xs font-medium rounded-full ${getPriorityColor(
                              msg.priority
                            )}`}
                          >
                            {msg.priority.charAt(0).toUpperCase() +
                              msg.priority.slice(1)}
                          </span>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                          <span className="flex items-center">
                            <MessageSquare className="w-4 h-4 mr-1" />
                            {getCategoryLabel(msg.category)}
                          </span>
                          <span className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {formatDate(msg.created_at)}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4 flex-shrink-0">
                        {msg.admin_response && (
                          <span className="inline-flex items-center px-3 py-1 text-sm text-green-800 bg-green-100 rounded-full">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Replied
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Original Message */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-3">
                      <div className="flex items-center mb-2">
                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium mr-3">
                          You
                        </div>
                        <span className="text-sm font-medium text-gray-700">
                          Your Message
                        </span>
                      </div>
                      <p className="text-gray-700 whitespace-pre-wrap leading-relaxed pl-11">
                        {msg.message}
                      </p>
                    </div>

                    {/* Admin Response */}
                    {msg.admin_response && (
                      <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-400">
                        <div className="flex items-center mb-2">
                          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium mr-3">
                            <MessageSquare className="w-4 h-4" />
                          </div>
                          <div className="flex-1">
                            <span className="text-sm font-medium text-blue-900">
                              Support Team Response
                            </span>
                            <p className="text-xs text-blue-600">
                              Replied on {formatDate(msg.admin_response_at)}
                            </p>
                          </div>
                        </div>
                        <p className="text-blue-900 whitespace-pre-wrap leading-relaxed pl-11">
                          {msg.admin_response}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Send New Message Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="mr-3 rounded-full bg-green-100 p-2 text-green-600">
              <MessageSquare className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Send New Message
              </h3>
              <p className="text-sm text-gray-500">
                Need help? Send us a message and we'll get back to you soon!
              </p>
            </div>
          </div>
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
            Subscriber Feature
          </span>
        </div>

        {/* Toggle Button */}
        {!isOpen && (
          <button
            onClick={() => setIsOpen(true)}
            className="w-full flex items-center justify-center px-4 py-3 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-4 focus:ring-green-300"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Send New Message
            <ChevronDown className="w-4 h-4 ml-2" />
          </button>
        )}

        {/* Message Form */}
        {isOpen && (
          <div className="border border-gray-200 rounded-lg p-4">
            {success ? (
              <div className="text-center py-6">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                <h4 className="text-lg font-medium text-gray-900 mb-2">
                  Message Sent Successfully!
                </h4>
                <p className="text-sm text-gray-600">
                  We've received your message and will get back to you soon.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Category and Priority */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {categories.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Priority
                    </label>
                    <select
                      name="priority"
                      value={formData.priority}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject (Optional)
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="Brief description of your inquiry"
                    maxLength={500}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={6}
                    placeholder="Please describe your question, issue, or feedback in detail..."
                    maxLength={5000}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                  />
                  <div className="flex justify-between mt-1">
                    <p className="text-xs text-gray-500">
                      Please be as detailed as possible to help us assist you
                      better.
                    </p>
                    <p className="text-xs text-gray-500">
                      {formData.message.length}/5000
                    </p>
                  </div>
                </div>

                {/* Error Display */}
                {error && (
                  <div className="flex items-center p-3 text-sm text-red-700 bg-red-100 rounded-lg">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    {error}
                  </div>
                )}

                {/* Buttons */}
                <div className="flex items-center justify-between pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setIsOpen(false);
                      setError(null);
                      setFormData({
                        subject: "",
                        message: "",
                        category: "general",
                        priority: "normal",
                      });
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !formData.message.trim()}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Clock className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PremiumMessaging;
